import { LoadingButton } from "@mui/lab";
import { Box, Grid, Typography } from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { useContext, useState } from "react";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { trackEvent } from "utils/tracking";

import { OutreachAutomationType } from "../../../types";

const SOURCE_OPTIONS: {
  type: OutreachAutomationType;
  icon: string;
  iconBg: string;
  iconColor?: string;
  title: string;
  description: string;
  volume: string;
}[] = [
  {
    type: "bento_database",
    icon: "fa-solid fa-database",
    iconBg: "#e8f4fd",
    iconColor: "#1976d2",
    title: "Bento Brand Database",
    description:
      "Discover brands from our curated database matched to your niche and preferences.",
    volume: "~10-50 brands/week",
  },
  {
    type: "instagram_tracked_creators",
    icon: "fa-brands fa-instagram",
    iconBg: "#fce8ec",
    iconColor: "#c03d5e",
    title: "Instagram Creator",
    description:
      "Find brands that are already working with creators similar to you on Instagram.",
    volume: "~2-8 brands/week",
  },
  {
    type: "google_places",
    icon: "fa-solid fa-location-dot",
    iconBg: "#d5f0e5",
    iconColor: "#1a7a52",
    title: "Local Business Search",
    description:
      "Discover local businesses near you that could be great brand partners.",
    volume: "~15-40 brands/week",
  },
];

const COMING_SOON_OPTIONS: {
  id: string;
  icon: string;
  iconBg: string;
  iconColor?: string;
  title: string;
  description: string;
  volume: string;
}[] = [
  {
    id: "tiktok_tracked_creators",
    icon: "fa-brands fa-tiktok",
    iconBg: "#f1f1f1",
    iconColor: "#010101",
    title: "TikTok Creator",
    description:
      "Find brands that are already working with creators similar to you on TikTok.",
    volume: "~2-8 brands/week",
  },
  {
    id: "instagram_hashtag_search",
    icon: "fa-solid fa-hashtag",
    iconBg: "#fce8ec",
    iconColor: "#c03d5e",
    title: "Instagram Hashtag Search",
    description:
      "Discover brands posting to specific Instagram hashtags in your niche.",
    volume: "~5-20 brands/week",
  },
  {
    id: "tiktok_hashtag_search",
    icon: "fa-solid fa-hashtag",
    iconBg: "#f1f1f1",
    iconColor: "#010101",
    title: "TikTok Hashtag Search",
    description:
      "Discover brands posting to specific TikTok hashtags in your niche.",
    volume: "~5-20 brands/week",
  },
  {
    id: "email_inbox",
    icon: "fa-solid fa-envelope-open-text",
    iconBg: "#fff4e5",
    iconColor: "#b45309",
    title: "Find Brands From Your Email",
    description:
      "Scan your inbox for brands you've already been in touch with and surface new pitches.",
    volume: "Varies by inbox",
  },
];

interface SourceSelectionProps {
  onNext: (type: OutreachAutomationType) => void | Promise<void>;
  initialType?: OutreachAutomationType | null;
}

export default function SourceSelection({
  onNext,
  initialType,
}: SourceSelectionProps) {
  const { discoverTab } = useContext(DiscoverViewContext);
  const sidebarOpen = discoverTab !== null;
  const [selectedType, setSelectedType] =
    useState<OutreachAutomationType | null>(initialType ?? null);
  const [selectedComingSoon, setSelectedComingSoon] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [comingSoonDialogTitle, setComingSoonDialogTitle] = useState<
    string | null
  >(null);

  const handleRealSourceClick = (
    type: OutreachAutomationType,
    _title: string,
  ) => {
    setSelectedType(type);
    setSelectedComingSoon(null);
  };

  const handleComingSoonClick = (id: string, title: string) => {
    setSelectedComingSoon({ id, title });
    setSelectedType(null);
  };

  const hasSelection = selectedType !== null || selectedComingSoon !== null;

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", mt: 4 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 22,
          color: "primary.main",
          textAlign: "center",
          mb: 1,
        }}
      >
        Choose Your Brand Source
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "rgba(1,67,57,0.6)",
          textAlign: "center",
          mb: 4,
        }}
      >
        Select where you want to discover brands from
      </Typography>

      <Grid container spacing={2}>
        {SOURCE_OPTIONS.map((option) => {
          const isSelected = selectedType === option.type;
          return (
            <Grid
              item
              xs={12}
              sm={sidebarOpen ? 12 : 6}
              md={sidebarOpen ? 6 : 4}
              key={option.type}
            >
              <Box
                onClick={() => handleRealSourceClick(option.type, option.title)}
                sx={{
                  backgroundColor: isSelected ? "rgba(1,67,57,0.03)" : "white",
                  border: "2px solid",
                  borderColor: isSelected
                    ? "primary.main"
                    : "rgba(1,67,57,0.1)",
                  borderRadius: "14px",
                  p: 3,
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 4px 12px rgba(1,67,57,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: option.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <Box
                    component="span"
                    className={option.icon}
                    sx={{ fontSize: 18, color: option.iconColor ?? "white" }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "primary.main",
                    mb: 0.5,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "rgba(1,67,57,0.6)",
                    lineHeight: "20px",
                    mb: 1.5,
                  }}
                >
                  {option.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "rgba(1,67,57,0.5)",
                  }}
                >
                  {option.volume}
                </Typography>
              </Box>
            </Grid>
          );
        })}

        {COMING_SOON_OPTIONS.map((option) => {
          const isSelected = selectedComingSoon?.id === option.id;
          return (
            <Grid
              item
              xs={12}
              sm={sidebarOpen ? 12 : 6}
              md={sidebarOpen ? 6 : 4}
              key={option.id}
            >
              <Box
                onClick={() => handleComingSoonClick(option.id, option.title)}
                sx={{
                  backgroundColor: isSelected ? "rgba(1,67,57,0.03)" : "white",
                  border: "2px solid",
                  borderColor: isSelected
                    ? "primary.main"
                    : "rgba(1,67,57,0.1)",
                  borderRadius: "14px",
                  p: 3,
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 4px 12px rgba(1,67,57,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: option.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <Box
                    component="span"
                    className={option.icon}
                    sx={{ fontSize: 18, color: option.iconColor ?? "white" }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "primary.main",
                    mb: 0.5,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "rgba(1,67,57,0.6)",
                    lineHeight: "20px",
                    mb: 1.5,
                  }}
                >
                  {option.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "rgba(1,67,57,0.5)",
                  }}
                >
                  {option.volume}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <ConfirmDialog
        open={comingSoonDialogTitle !== null}
        header="Coming soon"
        subheader={
          comingSoonDialogTitle
            ? `${comingSoonDialogTitle} isn't available yet, but it's on our roadmap. We'll let you know as soon as it's ready!`
            : ""
        }
        handleClose={() => setComingSoonDialogTitle(null)}
        handleConfirm={() => setComingSoonDialogTitle(null)}
        confirmText="Got it"
      />

      <LoadingButton
        variant="contained"
        fullWidth
        loading={loading}
        disabled={!hasSelection}
        onClick={async () => {
          if (selectedComingSoon) {
            trackEvent("Autopilot Source Selected", {
              "Source ID": selectedComingSoon.id,
              "Source Title": selectedComingSoon.title,
              "Coming Soon": true,
            });
            setComingSoonDialogTitle(selectedComingSoon.title);
            return;
          }
          if (!selectedType) return;
          const selectedOption = SOURCE_OPTIONS.find(
            (o) => o.type === selectedType,
          );
          trackEvent("Autopilot Source Selected", {
            "Source ID": selectedType,
            "Source Title": selectedOption?.title ?? selectedType,
            "Coming Soon": false,
          });
          setLoading(true);
          try {
            await onNext(selectedType);
          } finally {
            setLoading(false);
          }
        }}
        sx={{
          mt: 4,
          maxWidth: 400,
          mx: "auto",
          display: "block",
          borderRadius: "10px",
          backgroundColor: "primary.main",
          fontWeight: 600,
          fontSize: 16,
          py: 1.5,
          textTransform: "none",
        }}
      >
        Next
      </LoadingButton>
    </Box>
  );
}
